import React, { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { formatRelativeDate } from '@/lib/utils'
import { MessageSquare, Trash2, Edit3, Check, X } from 'lucide-react'
import { toast } from 'sonner'
import Avatar from '@/components/Avatar'

/**
 * NoteThread — threaded internal notes on a contact or company.
 *
 * Backed by the `entity_notes` table (Session 34, Chunk 2). The component is
 * polymorphic over `entityType`; it does not care which detail page mounted
 * it. RLS handles tenant isolation and the author-or-admin moderation rule;
 * the component just talks to the table.
 *
 * The page-level `notes` textarea is intentionally preserved as a quick
 * summary; this thread is the place for dated, attributable observations.
 */

interface Props {
  entityType: 'contact' | 'company'
  entityId: string
}

interface NoteRow {
  id: string
  workspace_id: string
  entity_type: string
  entity_id: string
  author_id: string | null
  body: string
  created_at: string
  updated_at: string
}

interface AuthorRow {
  id: string
  full_name: string | null
}

const queryKey = (type: string, id: string) => ['entity-notes', type, id] as const

export default function NoteThread({ entityType, entityId }: Props) {
  const queryClient = useQueryClient()
  const { profile, workspace } = useAuth()
  const [draft, setDraft] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editDraft, setEditDraft] = useState('')

  const isAdmin = !!profile && (profile.is_owner || profile.role === 'admin')
  const meId = profile?.id ?? null
  const workspaceId = workspace?.id ?? null

  const { data: notes = [], isLoading } = useQuery<NoteRow[]>({
    queryKey: queryKey(entityType, entityId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('entity_notes')
        .select('*')
        .eq('entity_type', entityType)
        .eq('entity_id', entityId)
        .order('created_at', { ascending: false })
      if (error) throw error
      return (data ?? []) as NoteRow[]
    },
    enabled: !!entityId,
  })

  // Resolve author display names in a single follow-up query. RLS on profiles
  // already scopes to workspace, so we don't need to filter further.
  const authorIds = useMemo(
    () => Array.from(new Set(notes.map(n => n.author_id).filter((v): v is string => !!v))),
    [notes],
  )
  const { data: authors = [] } = useQuery<AuthorRow[]>({
    queryKey: ['entity-notes-authors', authorIds.sort().join(',')],
    queryFn: async () => {
      if (authorIds.length === 0) return []
      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name')
        .in('id', authorIds)
      if (error) throw error
      return (data ?? []) as AuthorRow[]
    },
    enabled: authorIds.length > 0,
    staleTime: 60_000,
  })
  const authorById = useMemo(() => {
    const m = new Map<string, AuthorRow>()
    for (const a of authors) m.set(a.id, a)
    return m
  }, [authors])

  const createMutation = useMutation({
    mutationFn: async (body: string) => {
      if (!workspaceId || !meId) throw new Error('Not signed in')
      const { error } = await supabase.from('entity_notes').insert({
        workspace_id: workspaceId,
        entity_type: entityType,
        entity_id: entityId,
        author_id: meId,
        body,
      })
      if (error) throw error
    },
    onSuccess: () => {
      setDraft('')
      queryClient.invalidateQueries({ queryKey: queryKey(entityType, entityId) })
    },
    onError: (e: any) => toast.error(e?.message || 'Failed to add note'),
  })

  const updateMutation = useMutation({
    mutationFn: async (vars: { id: string; body: string }) => {
      const { error } = await supabase
        .from('entity_notes')
        .update({ body: vars.body, updated_at: new Date().toISOString() })
        .eq('id', vars.id)
      if (error) throw error
    },
    onSuccess: () => {
      setEditingId(null)
      setEditDraft('')
      queryClient.invalidateQueries({ queryKey: queryKey(entityType, entityId) })
    },
    onError: (e: any) => toast.error(e?.message || 'Failed to update note'),
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('entity_notes').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKey(entityType, entityId) }),
    onError: (e: any) => toast.error(e?.message || 'Failed to delete note'),
  })

  const submit = () => {
    const body = draft.trim()
    if (!body || createMutation.isPending) return
    createMutation.mutate(body)
  }

  const startEdit = (n: NoteRow) => {
    setEditingId(n.id)
    setEditDraft(n.body)
  }

  const saveEdit = () => {
    if (!editingId) return
    const body = editDraft.trim()
    if (!body) return
    updateMutation.mutate({ id: editingId, body })
  }

  const cancelEdit = () => {
    setEditingId(null)
    setEditDraft('')
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, padding: 14 }}>
        <textarea
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); submit() }
          }}
          placeholder="Add an internal note. Visible to your team only. ⌘↩ to post."
          rows={3}
          style={{ width: '100%', padding: '10px 12px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 9, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.5, fontFamily: 'inherit' }}
          onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
          onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
          <button
            type="button"
            onClick={submit}
            disabled={!draft.trim() || createMutation.isPending}
            className="btn-primary"
            style={{ opacity: !draft.trim() || createMutation.isPending ? 0.5 : 1, cursor: !draft.trim() || createMutation.isPending ? 'not-allowed' : 'pointer' }}
          >
            {createMutation.isPending ? 'Posting…' : 'Post note'}
          </button>
        </div>
      </div>

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 24, color: 'var(--ink-tertiary)', fontSize: 12 }}>Loading notes…</div>
      ) : notes.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <MessageSquare style={{ width: 28, height: 28, color: 'var(--ink-tertiary)', margin: '0 auto 10px', display: 'block' }} />
          <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: 0 }}>No notes yet</p>
          <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: '4px 0 0' }}>The first note from your team will appear here.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <AnimatePresence initial={false}>
            {notes.map(n => {
              const author = n.author_id ? authorById.get(n.author_id) : null
              const authorName = author?.full_name || (n.author_id ? 'Teammate' : 'Former teammate')
              const canEdit = !!meId && n.author_id === meId
              const canDelete = canEdit || isAdmin
              const edited = n.updated_at && n.updated_at !== n.created_at
              const isEditing = editingId === n.id
              return (
                <motion.div
                  key={n.id}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.98 }}
                  transition={{ duration: 0.14 }}
                  style={{ display: 'flex', gap: 12, padding: 14, background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 12 }}
                >
                  <Avatar name={authorName} size={32} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)' }}>{authorName}</span>
                      <span style={{ fontSize: 11, color: 'var(--ink-tertiary)' }}>{formatRelativeDate(n.created_at)}</span>
                      {edited && <span style={{ fontSize: 11, color: 'var(--ink-tertiary)' }}>· edited</span>}
                      <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
                        {canEdit && !isEditing && (
                          <button onClick={() => startEdit(n)} aria-label="Edit note"
                            style={{ padding: 4, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', display: 'flex', borderRadius: 6 }}
                            onMouseEnter={e => e.currentTarget.style.background = 'var(--hover-bg)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                            <Edit3 style={{ width: 13, height: 13 }} />
                          </button>
                        )}
                        {canDelete && !isEditing && (
                          <button onClick={() => { if (window.confirm('Delete this note?')) deleteMutation.mutate(n.id) }} aria-label="Delete note"
                            style={{ padding: 4, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', display: 'flex', borderRadius: 6 }}
                            onMouseEnter={e => { e.currentTarget.style.background = 'var(--hover-bg)'; e.currentTarget.style.color = 'var(--danger)' }}
                            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--ink-tertiary)' }}>
                            <Trash2 style={{ width: 13, height: 13 }} />
                          </button>
                        )}
                      </div>
                    </div>

                    {isEditing ? (
                      <div>
                        <textarea
                          autoFocus
                          value={editDraft}
                          onChange={e => setEditDraft(e.target.value)}
                          onKeyDown={e => {
                            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); saveEdit() }
                            if (e.key === 'Escape') cancelEdit()
                          }}
                          rows={3}
                          style={{ width: '100%', padding: '8px 10px', background: 'var(--field-bg)', border: '1px solid var(--focus-ring)', borderRadius: 8, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.5, fontFamily: 'inherit' }}
                        />
                        <div style={{ display: 'flex', gap: 6, marginTop: 8, justifyContent: 'flex-end' }}>
                          <button onClick={cancelEdit}
                            style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 10px', background: 'var(--border-subtle)', border: 'none', borderRadius: 7, cursor: 'pointer', color: 'var(--ink-secondary)', fontSize: 12, fontWeight: 600 }}>
                            <X style={{ width: 12, height: 12 }} /> Cancel
                          </button>
                          <button onClick={saveEdit} disabled={!editDraft.trim() || updateMutation.isPending}
                            style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 10px', background: 'var(--accent)', border: 'none', borderRadius: 7, cursor: !editDraft.trim() ? 'not-allowed' : 'pointer', color: 'var(--accent-ink)', fontSize: 12, fontWeight: 600, opacity: !editDraft.trim() ? 0.5 : 1 }}>
                            <Check style={{ width: 12, height: 12 }} /> Save
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p style={{ margin: 0, fontSize: 13, color: 'var(--ink-secondary)', whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>{n.body}</p>
                    )}
                  </div>
                </motion.div>
              )
            })}
          </AnimatePresence>
        </div>
      )}
    </div>
  )
}
